<template>
  <transition name="modal">
    <div class="sword-modal-mask center-text">
      <div
        ref="modalContainer"
        class="sword-modal-container"
        :class="typeClass"
      >
        <div class="sword-modal-top-bar -pxy_2">
          <sword-icon
            name="close"
            class="modal-top-bar-icon"
            @click.native="$emit('close')">
          </sword-icon>
        </div>
        <div
          ref="modalContent"
          class="sword-modal-content -pt_6"
          :class="{ scrollable }"
          @scroll="setOrUnsetFooterShadow"
        >
          <slot name="content">
            <div class="sword-modal-header -mb_4 -px_6">
              <slot name="header">
                <h5 class="my_0 -t_heavy"> {{ $t(header) }} </h5>
              </slot>
            </div>
            <div class="sword-modal-body -mb_4 -px_6">
              <slot name="body">
                <img v-if="imageData" class="sword-modal-body-image" :src="imageData.src" :alt="imageData.altText">
                <p class="t2 -mb_0" v-if="body"> {{ $t(body) }} </p>
                <p class="t2 -mb_0" v-if="body2nd"> {{ $t(body2nd) }} </p>
                <p class="t2 -mb_0" v-if="body3rd"> {{ $t(body3rd) }} </p>
              </slot>
            </div>
          </slot>
          <div class="sword-modal-footer -py_3 -px_2" :class="{ scrollable, shadow: footerShadow }">
            <slot name="footer">
              <sword-button type="secondary"
                            icon="close"
                            side="left"
                            :text="leftBtText"
                            @click.native="$emit('cancel')">
              </sword-button>

              <sword-button icon="check"
                            :text="rightBtText"
                            @click.native="$emit('ok')">
              </sword-button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { useDebounceFn, onClickOutside } from '@vueuse/core';
import { blockBodyOverflow, unlockBodyOverflow } from '@ui-kit/scripts/utils/body-overflow';
import SwordButton from '@ui-kit/components/buttons/SwordButton.vue';
import SwordIcon from '@ui-kit/components/icons/SwordIcon.vue';


export default {
  name: 'swordModal',
  components: {
    SwordButton,
    SwordIcon,
  },
  props: {
    type: {
      type: String,
      default: 'info',
      validator(value) {
        return ['info', 'warning'].indexOf(value) !== -1;
      },
    },
    header: {
      type: String,
      required: true,
    },
    body: {
      type: String,
      required: false,
    },
    body2nd: {
      type: String,
      required: false,
    },
    body3rd: {
      type: String,
      required: false,
    },
    imgPath: {
      type: String,
      required: false,
    },
    imgAltExt: {
      type: String,
      required: false,
    },
    leftBtText: {
      type: String,
      default: 'copy_12',
      required: false,
    },
    rightBtText: {
      type: String,
      default: 'copy_11',
      required: false,
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    closeOnBack: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      footerShadow: false,
    };
  },
  computed: {
    typeClass() {
      return `--${this.type}-modal`;
    },
    imageData() {
      if (!this.imgPath) {
        return null;
      }
      return {
        src: this.imgPath,
        altText: this.imgAltExt || '',
      };
    },
    shouldCloseOnBack() {
      return this.closeOnBack && window.innerWidth <= 992;
    },
  },
  methods: {
    setOrUnsetFooterShadow: useDebounceFn(function setOrUnsetFooterShadowCallback() {
      if (!this.scrollable || !this.$refs.modalContent) this.footerShadow = false;

      if (this.$refs.modalContent && Object.values(this.$refs.modalContent).length) {
        const { scrollHeight, scrollTop, clientHeight } = this.$refs.modalContent;
        const maxScroll = scrollHeight - clientHeight;

        this.footerShadow = maxScroll > 0 && scrollTop < maxScroll;
      }
    }, 50),
    backButtonClicked: useDebounceFn(event => {
      if (event.state === null) {
        // eslint-disable-next-line no-restricted-globals
        history.pushState(null, document.title, window.location.pathname);
      }
    }, 50),
  },
  mounted() {
    blockBodyOverflow();

    this.setOrUnsetFooterShadow();
    if (this.scrollable) {
      window.addEventListener('resize', this.setOrUnsetFooterShadow);
    }

    if (this.shouldCloseOnBack) {
      // eslint-disable-next-line no-restricted-globals
      history.pushState(null, document.title, window.location.pathname);

      window.onpopstate = event => {
        this.backButtonClicked(event);
        setTimeout(() => {
          this.$emit('close');
        }, 50);
      };
    }

    onClickOutside(this.$refs.modalContainer, event => {
      this.$emit('close', event);
    });
  },
  beforeDestroy() {
    unlockBodyOverflow();

    if (this.scrollable) {
      window.removeEventListener('resize', this.setOrUnsetFooterShadow);
    }

    if (this.shouldCloseOnBack) {
      setTimeout(() => {
        // eslint-disable-next-line no-restricted-globals
        history.go(-1);
      }, 100);

      window.onpopstate = null;
    }
  }
};
</script>

<style lang="scss" scoped>
  .sword-modal {
    &-mask {
      position: fixed;
      z-index: 9998;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: grid;
      grid: min-content/auto;
      align-content: center;
      justify-content: center;
      transition: opacity 0.3s ease;
      background-color: $modal-backdrop-color;
    }

    &-top-bar {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 10;
      display: flex;
      justify-content: flex-end;

      .modal-top-bar-icon {
        width: 1.5rem;
        height: 1.5rem;
        cursor: pointer;
        color: $modal-top-bar-icon-color;
      }
    }

    &-container {
      position: relative;
      max-width: $modal-max-width-desktop;
      width: $modal-width;
      min-width: $modal-min-width-desktop;
      height: 100%;
      max-height: calc(100vh - 5rem);
      overflow: hidden;
      border-radius: $modal-radius;
      margin: 0 auto;
      background-color: $modal-container-background-color;
      transition: all 0.3s ease;
    }

    &-content.scrollable {
      height: 100%;
      overflow-y: auto;
      -ms-overflow-style: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }


    &-footer {
      background: $modal-footer-background-color;
      text-align: center;

      &.scrollable {
        position: sticky;
        bottom: 0;

        &.shadow {
          border-radius: $modal-radius;
          box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.05);
        }
      }
    }
  }

  /**
    Specific modal type styles
    */
  .sword-modal-container {
    &.--warning-modal {
      .sword-modal-header {
        color: $sword-dark-wuk;
      }
    }
  }

  @media screen and (max-width: break-wuk('medium', 'l')) {
    .sword-modal-container {
      max-width: $modal-max-width-mobile;
    }
  }

  @media screen and (max-width: break-wuk('small', 's')-1) {
    .sword-modal-container {
      max-height: calc(100vh - .5rem);
      min-width: 100vw;
      border-radius: 8px 8px 0 0;
    }

    .sword-modal-mask {
      transition: none;
      align-content: end;
    }

    .sh-default-buttons {
      margin: 0;
    }

    .sword-modal-footer.scrollable {
      border-radius: 0;
    }
  }


  /**
    Modal animation
   */

  .modal-enter {
    opacity: 0;
  }

  .modal-leave-active {
    opacity: 0;
  }

  .modal-enter .modal-container,
  .modal-leave-active .modal-container {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }


  // ### WIP FOR CONFIRMATION MODALS
  // DESERVES MORE WORK (SIZING ETC)
  .sword-modal-body-image {
    position: relative;
    width: 170px;
    height: auto;
    margin: 0 auto 25px;

    svg {
      width: inherit;
    }
  }
</style>
